import { useEffect, useState } from "react";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider, Theme } from "@mui/material/styles";
import { FormType, getIsoLocaleFromCountry, Locales } from "@simplyk/common";
import { useRouter } from "next/router";

import { LocaleProvider } from "../../../../common/contexts/LocaleContext";
import { useLogParamsEvent } from "../../../../common/hooks/amplitude/useLogParamsEvent";
import { useDisableSafariBackForwardCache } from "../../../../common/hooks/useDisableSafariBackForwardCache";
import { AmplitudeProvider } from "../../../../common/providers/AmplitudeProvider";
import { FrontendFormProvider } from "../../../../common/providers/FrontendFormProvider";
import { PageLoadingProvider } from "../../../../common/providers/PageLoadingProvider";
import { zeffyTheme } from "../../../../common/theme/theme";
import {
  DonationFormFrontendPageOutput,
  FormOrganizationOutput,
  TicketingFrontendPageOutput,
} from "../../../../common/types/trpc";
import { DevContextCopy } from "../../DevContextCopy/DevContextCopy";
import { NotificationBannerHeightProvider } from "../../NotificationBanners/NotificationBannerHeightContext";
import { PublicRoute } from "../../Routes/PublicRoute";

import { useGtmFormView } from "./useGtmFormView";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

import { ZeffySnackbarProvider } from "@/dashboard-design-system/Snackbar";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

export const AppFormProvider = ({
  children,
  theme,
  organization,
  formData,
  formType,
  fields,
  isEmbed,
  themeColor,
  SSREnabled = true,
  isDisplayOnly,
  hideNotificationBanners,
}: {
  children: React.ReactNode;
  theme: Theme;
  themeColor: string | null;
  organization: FormOrganizationOutput | null;
  fields: { locale: Locales }[];
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput | null;
  formType: FormType | null;
  isEmbed?: boolean;
  SSREnabled?: boolean;
  isDisplayOnly?: boolean;
  hideNotificationBanners?: boolean;
}) => {
  useLogParamsEvent();
  useDisableSafariBackForwardCache();
  const router = useRouter();
  const forcedLocale = fields?.length === 1 ? fields[0].locale : undefined;
  const forcedIsoLocale = forcedLocale ? getIsoLocaleFromCountry(organization?.country, forcedLocale) : undefined;

  const [key, setKey] = useState(0);
  // Disable SSR as it creates memory leaks
  useEffect(() => {
    if (!SSREnabled) {
      setKey(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useGtmFormView({
    formId: formData?.id,
    formPath: formData?.path,
    formType,
    organizationId: organization?.id,
  });

  if (key === 0 && !SSREnabled) {
    return <DashboardZeffyLoader />;
  }

  return (
    <>
      <PageLoadingProvider isLoading={router.isFallback} disableLoading>
        <ThemeProvider theme={theme || zeffyTheme}>
          <NotificationBannerHeightProvider>
            <CssBaseline />
            <AmplitudeProvider>
              <LocaleProvider forcedIsoLocale={forcedIsoLocale} formDefaultLocale={formData?.locale}>
                <ZeffySnackbarProvider>
                  <DevContextCopy formId={formData?.id || null} />
                  <PublicRoute>
                    <FrontendFormProvider
                      formData={formData}
                      organization={organization}
                      formType={formType}
                      isEmbed={isEmbed || false}
                      themeColor={themeColor || ""}
                      isDisplayOnly={isDisplayOnly}
                      hideNotificationBanners={hideNotificationBanners}
                    >
                      {children}
                    </FrontendFormProvider>
                  </PublicRoute>
                </ZeffySnackbarProvider>
              </LocaleProvider>
            </AmplitudeProvider>
          </NotificationBannerHeightProvider>
        </ThemeProvider>
      </PageLoadingProvider>
    </>
  );
};
